/**
 * Skill icon mapping for all technologies and programming languages used in the portfolio
 * Maps skill names to their corresponding devicon class names, custom image paths, or Lucide React icon components
 * Only includes verified devicon classes that exist in the CDN
 */
export const SKILL_ICON_MAP: Record<string, string> = {
  // Programming Languages (using mix of custom logos and devicon with CSS coloring)
  'Java': 'devicon-java-plain',
  'Python': '/assets/skill-icons/Python_logo_icon.png', // New custom Python logo
  'C#': 'devicon-csharp-plain',
  'SQL': 'devicon-sqlite-plain', // Using SQLite as generic SQL icon
  'JavaScript': 'devicon-javascript-plain',
  'TypeScript': 'devicon-typescript-plain',
  'Go': 'devicon-go-plain',
  'XAML': 'devicon-dotnetcore-plain', // Using .NET Core for XAML (Microsoft technology)
  'HTML': 'devicon-html5-plain',
  'CSS': 'devicon-css3-plain',
  'Shell': 'devicon-bash-plain',
  'PHP': 'devicon-php-plain',
  'Rust': 'devicon-rust-plain',
  'Swift': 'devicon-swift-plain',
  'Kotlin': 'devicon-kotlin-plain',
  'Dart': 'devicon-dart-plain',
  'Ruby': 'devicon-ruby-plain',
  'C++': 'devicon-cplusplus-plain',
  'C': 'devicon-c-original',
  'Makefile': '/assets/skill-icons/makefile.png', // Custom image
  
  // Frameworks & Tools (using custom logos and devicon with CSS coloring)
  'Databricks': '/assets/skill-icons/databricks-logo.png', // Custom Databricks logo
  'Apache Spark': 'devicon-apachespark-original', // Keep original as it has good styling
  'Spark': 'devicon-apachespark-original', // Keep original as it has good styling
  'Delta Lake': '/assets/skill-icons/delta-lake-logo.png', // Updated to use new Delta Lake logo
  'Databricks Asset Bundles': '/assets/skill-icons/databricks-logo.png', // Use Databricks logo
  'WPF': 'devicon-dotnetcore-plain', // Using .NET as WPF fallback
  'OpenAI': '/assets/skill-icons/openai.png', // Custom image
  'LeanIX': '/assets/skill-icons/leanix-logo.png', // New LeanIX logo
  'Streamlit': 'devicon-streamlit-plain',
  'Git': 'devicon-git-plain',
  'GitHub': 'devicon-github-original', // Keep original for better icon
  'GitHub Copilot': 'devicon-github-original',
  'VS Code': 'devicon-vscode-plain',
  'VS Code Extensions': 'devicon-vscode-plain',
  'Docker': 'devicon-docker-plain',
  'Kubernetes': 'devicon-kubernetes-plain',
  'Jenkins': 'devicon-jenkins-plain',
  'npm': 'devicon-npm-original-wordmark', // Keep wordmark for better visibility
  'Webpack': 'devicon-webpack-plain',
  'Vite': 'devicon-vite-original', // Keep original for better styling
  'React': 'devicon-react-original', // Keep original for better styling
  'Vue': 'devicon-vuejs-plain',
  'Angular': 'devicon-angularjs-plain',
  'Node.js': 'devicon-nodejs-plain',
  'Express': 'devicon-express-original', // Keep original for better styling
  'MongoDB': 'devicon-mongodb-plain',
  'PostgreSQL': 'devicon-postgresql-plain',
  'MySQL': 'devicon-mysql-original', // Keep original for better styling
  'Redis': 'devicon-redis-plain',
  'Nginx': 'devicon-nginx-original', // Keep original for better styling
  'Apache': 'devicon-apache-plain',
  'Linux': 'devicon-linux-plain',
  'Ubuntu': 'devicon-ubuntu-plain',
  'Windows': 'devicon-windows8-original', // Keep original for better styling
  'macOS': 'devicon-apple-original', // Keep original for better styling
  'AWS': 'devicon-amazonwebservices-original', // Keep original for better styling
  'Azure': 'devicon-azure-plain',
  'Google Cloud': 'devicon-googlecloud-plain',
  'Terraform': 'devicon-terraform-plain',
  'Ansible': 'devicon-ansible-plain',
  'Unity Catalog': '/assets/skill-icons/databricks-logo.png', // Updated to use Databricks logo
  'Homebrew': 'devicon-homebrew-plain',
  'CLI': 'devicon-bash-plain',
  'Jupyter': '/assets/skill-icons/jupyter.png',
  'pandas': 'devicon-pandas-plain',
  'NumPy': 'devicon-numpy-plain',
  'scikit-learn': 'devicon-scikitlearn-original', // Keep original for better styling
  'TensorFlow': 'devicon-tensorflow-original', // Keep original for better styling
  'PyTorch': 'devicon-pytorch-original', // Keep original for better styling
  'matplotlib': 'devicon-matplotlib-plain',
  
  // Development Tools & IDEs (using plain variants with CSS coloring)
  'IntelliJ IDEA': 'devicon-intellij-plain',
  'Eclipse': 'devicon-eclipse-plain',
  'Visual Studio': 'devicon-visualstudio-plain',
  'Atom': 'devicon-atom-original', // Keep original for better styling
  'Vim': 'devicon-vim-plain',
  
  // Version Control & CI/CD (using plain variants with CSS coloring)
  'GitLab': 'devicon-gitlab-plain',
  'GitHub Actions': 'devicon-githubactions-plain',
  'CircleCI': 'devicon-circleci-plain',
  'CI/CD': 'devicon-githubactions-plain',
  
  // Testing Frameworks (using plain variants with CSS coloring)
  'Jest': 'devicon-jest-plain',
  'Mocha': 'devicon-mocha-plain',
  'Cypress': 'devicon-cypressio-plain',
  'Selenium': 'devicon-selenium-original', // Keep original for better styling
  'pytest': '/assets/skill-icons/pytest.png',
  
  // Build Tools (using plain variants with CSS coloring)
  'Maven': 'devicon-maven-plain',
  'Gradle': 'devicon-gradle-original', // Keep original for better styling
  'CMake': 'devicon-cmake-plain',
  
  // Monitoring & Analytics (using originals where they have better styling)
  'Grafana': 'devicon-grafana-original',
  'ElasticSearch': 'devicon-elasticsearch-plain',
  'Splunk': 'devicon-splunk-original',
  
  // Design & Documentation (using plain variants with CSS coloring)
  'Figma': 'devicon-figma-plain',
  'Adobe XD': 'devicon-xd-plain',
  'Photoshop': 'devicon-photoshop-plain',
  'Illustrator': 'devicon-illustrator-plain',
  'Notion': 'devicon-notion-plain',
  
  // Package Managers (using mix of plain and original for best styling)
  'yarn': 'devicon-yarn-plain',
  'pip': '/assets/skill-icons/Python_logo_icon.png', // Using new Python logo for pip
  'conda': 'devicon-anaconda-original', // Keep original for better styling
  'composer': 'devicon-composer-line',
  
  // Mobile Development (verified in devicon)
  'Android': 'devicon-android-plain',
  'iOS': 'devicon-apple-original',
  'Flutter': 'devicon-flutter-plain',
  'React Native': 'devicon-react-original',
  'Ionic': 'devicon-ionic-original',
  
  // Databases & Data (verified in devicon)
  'SQLite': 'devicon-sqlite-original',
  'Oracle': 'devicon-oracle-original',
  'Microsoft SQL Server': '/assets/skill-icons/sql_server.png',
  'DynamoDB': 'devicon-dynamodb-original',
  'Cassandra': 'devicon-cassandra-plain',
  'Neo4j': 'devicon-neo4j-plain',
  'InfluxDB': 'devicon-influxdb-original',
  
  // AI/ML specific tools
  'Hugging Face': '/assets/skill-icons/Python_logo_icon.png', // Using new Python logo
  'LangChain': '/assets/skill-icons/Python_logo_icon.png', // Using new Python logo
  'OpenCV': 'devicon-opencv-original',
  
  // Business & Enterprise Tools (verified in devicon)
  'Salesforce': 'devicon-salesforce-plain',
  'Jira': 'devicon-jira-plain',
  
  // Messaging & Communication (verified in devicon)
  'RabbitMQ': 'devicon-rabbitmq-original',
  'Apache Kafka': 'devicon-apachekafka-original',
  'Kafka': 'devicon-apachekafka-original',
  
  // Gaming & 3D (verified in devicon)
  'Unity': 'devicon-unity-original',
  'Blender': 'devicon-blender-original',
  
  // Missing Languages (using devicon with CSS coloring)
  'YAML': 'devicon-yaml-plain',
  
  // Missing Tools/Technologies (using custom skill icons and devicon)
  '.NET Framework': 'devicon-dotnetcore-plain',
  'Visual Studio Enterprise': '/assets/skill-icons/vs_enterprise.png',
  'Azure DevOps': '/assets/skill-icons/azure-devops-logo.png',
  'Confluence': '/assets/skill-icons/confluence.png',
  'Typer CLI': '/assets/skill-icons/typer.png',
  'Typer': '/assets/skill-icons/typer.png',
  'Jupyter Notebooks': '/assets/skill-icons/jupyter.png',
  'dbt': '/assets/skill-icons/dbt.png',
  'Power BI': '/assets/skill-icons/powerbi.png',
  'PowerBI': '/assets/skill-icons/powerbi.png',
  'SQL Server': '/assets/skill-icons/sql_server.png',
  
  // Enterprise/Governance Tools (using custom skill icons)
  'Atlan': '/assets/skill-icons/atlan.png',
  'DataHub': '/assets/skill-icons/datahub.png',
  'datahub': '/assets/skill-icons/datahub.png',
  'Open Metadata': '/assets/skill-icons/openmetadata.png',
  'OpenMetadata': '/assets/skill-icons/openmetadata.png',
  'OvalEdge': '/assets/skill-icons/ovaledge.png',
  'Purview': 'devicon-azure-plain', // Microsoft data governance, using Azure icon
  'COBIT': '/assets/skill-icons/cobit.png',
  'ITIL': '/assets/skill-icons/itil.png',
  
  // Professional Skills - using Lucide React icons for consistency
  'Data Engineering': 'lucide:Database', // Data engineering and pipeline work
  'Software Development': 'lucide:Code', // Software development and coding
  'AI': 'lucide:Brain', // Artificial intelligence and machine learning
  'Data Analytics': 'lucide:BarChart3', // Analytics and data visualization
  'Platform Architecture': 'lucide:Building', // Architecture and infrastructure
  'Data Warehouse Migration': 'lucide:Database', // Database migration
  'Data Governance': 'lucide:Shield', // Governance and compliance
  'IaC': 'lucide:Cloud', // Infrastructure as Code 
  'Real-time Systems': 'lucide:Zap', // Real-time processing
  'Manufacturing Execution Systems': 'lucide:Factory', // Manufacturing systems
  'IT Governance': 'lucide:ShieldCheck', // IT governance and compliance
  'GDPR': 'lucide:ShieldAlert', // Data protection and privacy
  'Risk Management': 'lucide:AlertTriangle', // Risk assessment
  'Audit Management': 'lucide:ClipboardCheck', // Audits and compliance
  'Natural Language Processing': 'lucide:MessageSquare', // NLP and text processing
  'RAG Architecture': 'lucide:Brain', // AI/ML architecture
  'Fuzzy Logic': 'lucide:Shuffle', // Fuzzy logic and uncertainty
  'Control Systems': 'lucide:Settings', // System control
  'DevOps': 'lucide:GitBranch', // DevOps and CI/CD
  'Process Optimization': 'lucide:TrendingUp', // Process improvement
  'Technical Documentation': 'lucide:FileText', // Documentation
  'Machine Learning': 'lucide:Bot', // ML and AI
  'Neural Networks': 'lucide:Network', // Neural networks
  'Backpropagation': 'lucide:ArrowLeftRight', // Algorithm representation
  'Developer Tools': 'lucide:Wrench', // Development tools
  'CLI Development': 'lucide:Terminal', // Command line tools
  'AI Integration': 'lucide:Plug', // Integration and connectivity
  'Compliance': 'lucide:CheckCircle', // Compliance and certification
  'UI/UX Design': 'lucide:Palette', // User interface and experience design
  'Data Architecture': 'lucide:Database', // Data architecture and modeling
  'Performance Optimization': 'lucide:Zap', // Performance tuning and optimization
  'Infrastructure Management': 'lucide:Server', // Infrastructure and system management
  
  // Leadership Skills - using Lucide React icons
  'Project Management': 'lucide:Briefcase', // Project management
  'Engineering Leadership': 'lucide:Crown', // Leadership and management
  'Cross-functional Collaboration': 'lucide:Users', // Team collaboration
  'Agile Methodologies': 'lucide:RotateCcw', // Agile and iterative processes
  'Technical Mentoring': 'lucide:GraduationCap', // Mentoring and teaching
  'Team Leadership': 'lucide:UserCheck', // Team leadership
  'Strategic Planning': 'lucide:Target', // Strategic planning and goals
};

/**
 * Get the appropriate devicon class for a skill name
 * @param skillName - The name of the skill/technology
 * @returns The devicon class name or null if not found
 */
export function getSkillIcon(skillName: string): string | null {
  // Try exact match first
  if (SKILL_ICON_MAP[skillName]) {
    return SKILL_ICON_MAP[skillName];
  }
  
  // Try case-insensitive exact match
  const normalizedSkillName = skillName.toLowerCase();
  for (const [key, value] of Object.entries(SKILL_ICON_MAP)) {
    if (key.toLowerCase() === normalizedSkillName) {
      return value;
    }
  }
  
  // Try word boundary matches to avoid false positives (like "Go" matching "Governance")
  // Only match if the skill name appears as a complete word
  for (const [key, value] of Object.entries(SKILL_ICON_MAP)) {
    const keyLower = key.toLowerCase();
    const skillLower = normalizedSkillName;
    
    // Check if skill name contains the mapped key as a complete word
    const wordRegex = new RegExp('\\b' + skillLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b');
    if (wordRegex.test(keyLower)) {
      return value;
    }
    
    // Check if mapped key contains the skill name as a complete word
    const reverseWordRegex = new RegExp('\\b' + keyLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b');
    if (reverseWordRegex.test(skillLower)) {
      return value;
    }
  }
  
  return null;
}

/**
 * Get all available skill icons
 * @returns Array of skill names that have icons available
 */
export function getAvailableSkillIcons(): string[] {
  return Object.keys(SKILL_ICON_MAP);
}

/**
 * Check if a skill has an icon available
 * @param skillName - The name of the skill/technology
 * @returns Boolean indicating if icon is available
 */
export function hasSkillIcon(skillName: string): boolean {
  return getSkillIcon(skillName) !== null;
}
